/**
 * Picks the face cluster for a speaker with no face labels of their own in a
 * scene, by ruling out the faces that belong to the other speakers
 * (PLAT-11992). In a two-person video "not A" is B, whatever label the face
 * tracker gave B in this shot.
 */

import type { FaceDetection } from './types';

/** A cluster smaller than this share of the scene's detections is a passer-by, not the other speaker. */
export const MIN_OTHER_CLUSTER_SHARE = 0.15;

type Cluster = { boxIndices: number[] };

/**
 * - Another speaker's cluster is in the scene → the largest cluster holding
 *   none of their labels (a two-shot with A identified).
 * - No other speaker's cluster → the one qualifying cluster (a close-up), or
 *   null when several qualify, since nothing says which is the speaker.
 * - Null as well when only the other speakers are on screen (a reaction
 *   shot): the caller keeps its fallback rather than framing the listener.
 *
 * @param sceneDetections the detections the clusters were built from;
 *   `boxIndices` index into it.
 * @param requireSingleCandidate null unless exactly one face is left — with
 *   more than two speakers, several leftover faces means nothing says which
 *   one is talking (PLAT-12143).
 */
export function selectClusterExcludingLabels<C extends Cluster>(
  clusters: readonly C[],
  sceneDetections: ReadonlyArray<Pick<FaceDetection, 'label'>>,
  excludedLabels: ReadonlySet<string>,
  requireSingleCandidate = false,
): C | null {
  if (excludedLabels.size === 0 || sceneDetections.length === 0) return null;

  let hasExcludedCluster = false;
  const candidates: C[] = [];
  for (const cluster of clusters) {
    const isExcluded = cluster.boxIndices.some(index =>
      excludedLabels.has(sceneDetections[index]?.label),
    );
    if (isExcluded) {
      hasExcludedCluster = true;
    } else if (
      cluster.boxIndices.length / sceneDetections.length >=
      MIN_OTHER_CLUSTER_SHARE
    ) {
      candidates.push(cluster);
    }
  }

  if (candidates.length === 0) return null;
  if (requireSingleCandidate && candidates.length > 1) return null;
  if (!hasExcludedCluster && candidates.length > 1) return null;

  return candidates.reduce((best, cluster) =>
    cluster.boxIndices.length > best.boxIndices.length ? cluster : best,
  );
}
